import { Injectable, inject, isDevMode } from "@angular/core";
import { environment } from "../../../environments/environment";
import { DeviceService } from "./device.service";
import { Faro, getWebInstrumentations, initializeFaro, LogLevel } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { PersistenceResource } from "../../../infra/persistence";
import { Informations } from "../../pages/main-form/forms/informations/form/informations-form.types";

@Injectable({
  providedIn: 'root',
})
export class GrafanaFaroService {
  private readonly deviceService = inject(DeviceService);
  private readonly persistanceResource = inject(PersistenceResource);

  private faro: Faro | null = null;
  private isInitialized = false;

  initialize(): Promise<void> {
    return new Promise(resolve => {
      if (isDevMode()) {
        resolve();
        return;
      }

      if (this.isInitialized) {
        resolve();
        return;
      }

      if (!environment.grafanaFaroDataSource || !environment.grafanaFaroApiKey) {
        console.warn('Missing grafana config');
        resolve();
        return;
      }

      try {
        const shouldSample = Math.random() < 0.01;
        const device = this.deviceService.getDevice();

        this.faro = initializeFaro({
          url: `${environment.grafanaFaroDataSource}/collect`,
          apiKey: environment.grafanaFaroApiKey,
          app: {
            name: environment.appName,
            version: environment.version,
            environment: environment.envName,
            namespace: device,
          },
          trackResources: shouldSample,
          preventGlobalExposure: true,
          instrumentations: [
            ...(shouldSample ? [new TracingInstrumentation()] : []),
            ...getWebInstrumentations({
              captureConsole: true,
              enablePerformanceInstrumentation: shouldSample,
            }),
          ],
          sessionTracking: {
            enabled: true,
            persistent: true,
          },
          dedupe: true,
          isolate: true,
          batching: {
            enabled: true,
            sendTimeout: 120000,
            itemLimit: 200,
          },
        });

        this.isInitialized = true;

        resolve();
      } catch (error) {
        console.error('Failed to initialize grafana:', error);
        resolve();
      }
    });
  }

  private setDealFromInformations(informations: Partial<Informations>): void {
    if (!this.faro) {
      return;
    }

    try {
      this.faro.api.setUser({
        id: informations.deal_id ?? '',
        attributes: {
          email: informations.email ?? '',
          ip: informations.ipAddress ?? ''
        }
      });
    } catch (error) {
      console.debug('Failed to set grafana deal', error);
    }
  }

  setupTracking(): void {
    const informations = this.persistanceResource.getInformations();

    if (informations) {
      this.setDealFromInformations(informations);
    }
  }

  pushEvent(name: string, payload?: Record<string, string>): void {
    if (!this.faro) return;

    this.faro.api.pushEvent(name, payload);
  }

  pushLog(message: string, level: LogLevel = LogLevel.INFO, context?: Record<string, string>): void {
    if (!this.faro) return;

    this.faro.api.pushLog([message], { level, context });
  }

  pushError(error: Error, context?: Record<string, string>): void {
    if (!this.faro) return;

    this.faro.api.pushError(error, context);
  }
}
